/**
 * Per-arche "focus mode" chrome state — whether an arche's upper bars (the
 * platform Header rendered by ArcheLayout AND the arche's own ArcheHeader) are
 * hidden so the arche can run chrome-free.
 *
 * State is keyed by the canonical arche id (`arche_*` / `arc_*`), so hiding
 * chrome in one arche (e.g. email) never affects another (e.g. todo-lists);
 * each restores its own state on return and across reloads (localStorage-backed).
 *
 * A PLUGIN arche may also drive this through the SDK (`useFiasChrome` →
 * `chrome_set`), and may suppress the host's focus-mode bar to draw its own.
 * That bar is the only way back once the chrome is hidden, so PERSISTENCE
 * FOLLOWS SAFETY (owner decision 2026-10-22): an arche's hidden state is
 * written to storage only while the host bar is kept. With the bar suppressed
 * it is session-only, so a broken plugin collapse control costs the user at
 * most a reload, never a reload-proof trap. Suppression itself is never
 * persisted — a plugin re-declares it every load.
 *
 * Safety: this is NOT browser fullscreen — no Fullscreen API. The context ships
 * a working in-memory DEFAULT value, so `useChrome()` outside a provider never
 * throws (existing arche component tests render ArcheHeader without wrapping the
 * provider). All localStorage access is guarded — malformed JSON, unavailable
 * storage (SSR/test/private mode), or a write failure all fall back to an
 * in-memory map and must never break app startup.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'fias-chrome-hidden-by-arche';

type HiddenMap = Record<string, boolean>;

export interface ChromeContextValue {
  /** True if this arche currently has its upper chrome hidden. */
  isHeadersHidden: (archeId: string | undefined) => boolean;
  /** Flip the hidden state for one arche. */
  toggleHeaders: (archeId: string) => void;
  /** Force-show the chrome for one arche (the restore affordance). */
  showHeaders: (archeId: string) => void;
  /** Set the hidden state for one arche explicitly (the plugin lane). */
  setHeadersHidden: (archeId: string, hidden: boolean) => void;
  /**
   * True while a plugin arche has suppressed the host's focus-mode bar to
   * draw its own. Session-only; see the persistence note above.
   */
  isHostBarSuppressed: (archeId: string | undefined) => boolean;
  /** Suppress (true) or restore (false) the host's focus-mode bar for one arche. */
  setHostBarSuppressed: (archeId: string, suppressed: boolean) => void;
}

/** Read the persisted map, tolerating malformed/absent/unavailable storage. */
function readStored(): HiddenMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: HiddenMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (v === true) out[k] = true;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Persist only the `true` entries whose host bar is kept; never throw if
 * storage is unavailable. An arche whose bar is suppressed is left out, so a
 * reload restores its chrome.
 */
function writeStored(map: HiddenMap, suppressed: HiddenMap): void {
  try {
    const trimmed: HiddenMap = {};
    for (const [k, v] of Object.entries(map)) if (v && !suppressed[k]) trimmed[k] = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // storage unavailable / quota — state still lives in memory
  }
}

// In-memory no-op default → useChrome() without a provider is safe (returns
// "not hidden" and ignores mutations) rather than throwing — this keeps the many
// arche component tests that render ArcheHeader without a provider working. A
// MUTATION with no provider is a wiring bug (the provider is app-level), so warn
// in dev rather than fail silently.
function warnNoProvider(fn: string): void {
  if (import.meta.env.DEV) {
    console.warn(
      `[ChromeContext] ${fn}() called with no <ChromeProvider> mounted — no-op. ` +
        `It is mounted app-wide in App.tsx; this indicates a missing provider.`,
    );
  }
}

const defaultValue: ChromeContextValue = {
  isHeadersHidden: () => false,
  toggleHeaders: () => warnNoProvider('toggleHeaders'),
  showHeaders: () => warnNoProvider('showHeaders'),
  setHeadersHidden: () => warnNoProvider('setHeadersHidden'),
  isHostBarSuppressed: () => false,
  setHostBarSuppressed: () => warnNoProvider('setHostBarSuppressed'),
};

const ChromeContext = createContext<ChromeContextValue>(defaultValue);

/** Pure map update: set or clear one key, returning the same object when nothing changes. */
function withFlag(prev: HiddenMap, archeId: string, on: boolean): HiddenMap {
  if (Boolean(prev[archeId]) === on) return prev;
  const next = { ...prev };
  if (on) next[archeId] = true;
  else delete next[archeId];
  return next;
}

export function ChromeProvider({ children }: { children: ReactNode }) {
  const [hidden, setHidden] = useState<HiddenMap>(() => readStored());
  // Never read from storage: suppression is a plugin's per-load declaration.
  const [suppressed, setSuppressed] = useState<HiddenMap>({});

  // Persist on change (effect, not inside the updater — pure updaters + a single
  // write per change, safe under StrictMode double-invocation). Keyed on BOTH
  // maps: suppressing the bar while hidden must drop the entry from storage.
  useEffect(() => {
    writeStored(hidden, suppressed);
  }, [hidden, suppressed]);

  const isHeadersHidden = useCallback(
    (archeId: string | undefined) => (archeId ? hidden[archeId] === true : false),
    [hidden],
  );

  const toggleHeaders = useCallback((archeId: string) => {
    setHidden((prev) => withFlag(prev, archeId, !prev[archeId]));
  }, []);

  const showHeaders = useCallback((archeId: string) => {
    setHidden((prev) => withFlag(prev, archeId, false));
  }, []);

  const setHeadersHidden = useCallback((archeId: string, on: boolean) => {
    setHidden((prev) => withFlag(prev, archeId, on));
  }, []);

  const isHostBarSuppressed = useCallback(
    (archeId: string | undefined) => (archeId ? suppressed[archeId] === true : false),
    [suppressed],
  );

  const setHostBarSuppressed = useCallback((archeId: string, on: boolean) => {
    setSuppressed((prev) => withFlag(prev, archeId, on));
  }, []);

  const value = useMemo(
    () => ({
      isHeadersHidden,
      toggleHeaders,
      showHeaders,
      setHeadersHidden,
      isHostBarSuppressed,
      setHostBarSuppressed,
    }),
    [
      isHeadersHidden,
      toggleHeaders,
      showHeaders,
      setHeadersHidden,
      isHostBarSuppressed,
      setHostBarSuppressed,
    ],
  );

  return <ChromeContext.Provider value={value}>{children}</ChromeContext.Provider>;
}

export function useChrome(): ChromeContextValue {
  return useContext(ChromeContext);
}
